import './config-initial';
import {
    getBrandDomain,
    getBrandId,
    isJustDynamicBranding,
} from '@ringcentral/rwc-branding/src/utils';
import { readLocallyStored, writeLocallyStored } from '@ringcentral/rwc-utils/src/local-storage';
import { downloadFile } from '@ringcentral/rwc-utils/src/download';
import getConfig from '@ringcentral/rwc-utils/src/config';
import {
    Client,
    isVersionInIntervals,
    OS_INFO,
    UA_INFO,
    checkClient,
    isMac,
    isMobile,
    isElectron,
    getElectronVersion,
    compareVersions,
    isMicrosoftARMinBrowser,
} from '@ringcentral/rwc-utils/src/client-detection';
import { isAppleSiliconChip } from '@ringcentral/rwc-utils/src/gpu-info';
import { launchApplication } from '@low/application-detect';
import { makeOverridableFunction } from '@ringcentral/rwc-utils/src/override-api';

import { IS_CLIENT_SUPPORT, GET_LAUNCH_URL } from '@ringcentral/rwc-bridge';
import { featureFlagClient } from '@ringcentral/rwc-utils/src/feature-flag-client';
import { FEATURE_FLAGS } from '@ringcentral/rwc-utils/src/constants/feature-flags';
import { getPrefixedLogger, prefixes } from '@ringcentral/rwc-utils/src/rwc-logger';
import { removeFromQuery } from '@ringcentral/rwc-utils/src/urls/remove-from-query';
import { extractFromQueryString } from '@ringcentral/rwc-utils/src/url';

const MIN_ELECTRON_VERSION = '14';

const FROM_J_QUERY_NAME = 'fromJ';

const logger = getPrefixedLogger(prefixes.VC_LOW, 'scheme');

let allowUnsupportedBrowsers = false;
if (typeof localStorage === 'object') {
    /**
     * NOTE: deprecated
     * allowUnsupportedBrowsers is a back-door.
     * should remove it after all browser supported.
     *
     * before we support all browsers
     * check if current browser client has been GA
     * follow restriction roles if current browser client has been already GA
     * @param allowUnsupportedBrowsers
     */
    allowUnsupportedBrowsers =
        JSON.parse(localStorage.getItem('allowUnsupportedBrowsers')) || false;
    if (allowUnsupportedBrowsers) {
        // eslint-disable-next-line no-console
        console.warn('Browser restriction is disabled!');
    }
}

const getCapabilities = () => {
    const config = getConfig('CAPABILITIES');
    const result = config[OS_INFO] || config.default;
    const currentBrowserClientHasGeneralAvailability =
        (result.supported?.[UA_INFO.name] || []).length > 0;

    if (allowUnsupportedBrowsers && !currentBrowserClientHasGeneralAvailability) {
        return {
            ...result,
            supported: {
                [Client.CHROME]: [[]],
                [Client.FF]: [[]],
                [Client.SAFARI]: [[]],
                [Client.EDGE]: [[]],
                [Client.EDGE_CHROMIUM]: [[]],
                [Client.APPLICATION]: [[]],
            },
        };
    }

    return result;
};

/**
 * small hack for chromium compatibility checks for electron
 * we need this because we inside the code we need to check some browsers features
 * but different brands may have different app versions for same chrome versions
 */
export const isApplicationChromeCompatible = checkClient(Client.APPLICATION, {
    ...UA_INFO,
    version: UA_INFO.chromeVersion,
});

const isClientAllowed = client => (getCapabilities()?.supported?.[client] ?? []).length > 0;
export const isAppliationExists = () => isClientAllowed(Client.APPLICATION);
export const getSupportedBrowsersList = () =>
    Object.values(Client)
        .filter(isClientAllowed)
        .filter(client => client !== Client.APPLICATION)
        .sort();

export const isAnyVersionOfCurrentAllowed = () =>
    (getCapabilities()?.supported?.[UA_INFO.name] ?? []).length > 0;

export const isElectronVersionAllowed = () =>
    compareVersions(getElectronVersion(), MIN_ELECTRON_VERSION) >= 0;

export const isWebAllowed = () => getSupportedBrowsersList().length > 0;
export const isCurrentOSSupported = () => isWebAllowed() || isAppliationExists();
export const isCurrentClientOutdated = () =>
    !isCurrentClientSupported() && isAnyVersionOfCurrentAllowed();
export const isForceDownload = () => Boolean(getCapabilities()?.application?.forceDownload);

export const isMeetingIdDeepLinkSupported = () =>
    Boolean(getCapabilities()?.application?.meetingIdSupported);
export const getApplicationURIScheme = () => getCapabilities()?.application?.scheme ?? '';
export const canApplicationBeLaunched = () => Boolean(getApplicationURIScheme());

export const getApplicationDownloadLink = () => {
    const brandId = getBrandId();
    const brandProDomain = getBrandDomain();
    const capabilities = getCapabilities();
    let downloadLink = capabilities?.application?.downloadLink ?? '';
    const downloadM1Link = capabilities?.application?.downloadM1Link ?? '';
    const downloadWindowsARM64Link = capabilities?.application?.downloadWindowsARM64Link ?? '';

    if (isMicrosoftARMinBrowser()) {
        downloadLink = downloadWindowsARM64Link;
    }

    if (isMac && isAppleSiliconChip()) {
        downloadLink = downloadM1Link;
    }

    if (isJustDynamicBranding(downloadLink) && !isMobile) {
        if (downloadLink.includes('brandId')) return downloadLink;

        // set correct domain link for brand domains not in .com, like .biz
        const hostname = downloadLink.split('/')[2];
        const topLevelDomain = hostname.split('.').pop();
        if (!brandProDomain.endsWith(topLevelDomain)) {
            const brandTopLevelDomain = brandProDomain.split('.').pop();
            const brandHostname = hostname.replace(topLevelDomain, brandTopLevelDomain);
            downloadLink = downloadLink.replace(hostname, brandHostname);
        }

        return downloadLink ? `${downloadLink}&brandId=${brandId}` : '';
    }

    return downloadLink;
};

export const isCurrentClientSupported = makeOverridableFunction(() => {
    const isVersionAllowed = isVersionInIntervals(
        UA_INFO.version,
        getCapabilities().supported[UA_INFO.name]
    );

    if (isElectron()) {
        return isVersionAllowed && isElectronVersionAllowed();
    }

    return isVersionAllowed;
}, IS_CLIENT_SUPPORT);

const getLaunchUrl = makeOverridableFunction(uri => {
    const url = new URL(`${getApplicationURIScheme()}://${uri}`);
    if (!isMobile && location?.hostname && !url.searchParams.has('origin')) {
        url.searchParams.append('origin', location.hostname);
    }

    return url.toString();
}, GET_LAUNCH_URL);

/**
 * Tries to launch application
 *
 * @param {String} uri
 * @param {Function} successCb
 * @param {Function} errorCb
 * @param {Function} unknownCb
 * @param {Function} [cancelCb]
 */
export const tryLaunchApplication = (uri, successCb, errorCb, unknownCb, cancelCb) => {
    if (isAppliationExists()) {
        const scheme = getApplicationURIScheme();
        logger.info('scheme:', scheme);
        if (!scheme) {
            // eslint-disable-next-line no-console
            console.warn(
                'Desktop application scheme not found, Application should be started using deeplinking'
            );
            errorCb?.();
            return Promise.resolve();
        } else {
            logger.info('open url:', getLaunchUrl(uri));
            return launchApplication(getLaunchUrl(uri), successCb, errorCb, unknownCb, cancelCb);
        }
    } else {
        // eslint-disable-next-line no-console
        console.warn('Desktop application does not exist');
        return Promise.resolve();
    }
};

const getDownloadLinks = () => {
    const config = getConfig('BROWSERS');

    return {
        ...config.downloadLinks,
        [Client.APPLICATION]: getApplicationDownloadLink(),
    };
};

export const getClientDownloadLink = client => getDownloadLinks()[client];
export const getCurrentClientDownloadLink = () => getClientDownloadLink(UA_INFO.name);

export const getFileURLWithMeetingId = (fileURL, meetingId = null) => {
    const ext = fileURL.replace(/.*\.([a-zA-Z0-9]+)$/, '$1');
    if (!ext) return fileURL;

    const separator = fileURL.indexOf('?') < 0 ? '?' : '&';
    return `${fileURL}${meetingId ? separator + 'conf=' + meetingId : ''}`;
};

export const downloadApplication = (meetingId = null) => {
    let downloadLink = getApplicationDownloadLink();

    if (isMeetingIdDeepLinkSupported()) {
        downloadLink = getFileURLWithMeetingId(downloadLink, meetingId);
    }

    downloadFile(downloadLink);
};

/**
 * @returns {Boolean}
 */
export const isAlwaysJoinFromBrowserEnabled = () =>
    Boolean(readLocallyStored('prefs')?.alwaysJoinFromBrowser);

/**
 * @returns {Boolean}
 */
const isJoinFromWebInQuery = () => extractFromQueryString('joinFromWeb', '') === 'true';

const shouldUseMobileJoinFlow = () => {
    const useMobileJoinFLowFF = featureFlagClient.getFlag(FEATURE_FLAGS.useMobileJoinFlow);

    return isMobile && useMobileJoinFLowFF?.enabled;
};

export const shouldSkipLauncher = () =>
    shouldUseMobileJoinFlow() || isAlwaysJoinFromBrowserEnabled() || isJoinFromWebInQuery();

export const setAlwaysJoinFromBrowser = alwaysJoinFromBrowser => {
    const prefs = readLocallyStored('prefs') || {};
    writeLocallyStored('prefs', { ...prefs, alwaysJoinFromBrowser }, true);
};

export const setDeveloperFeatureFlag = value =>
    writeLocallyStored('developerFeatureFlag', value, true);

let cachedFromJ;
export const initAndGetFromJ = () => {
    if (cachedFromJ) return cachedFromJ;

    const fromJ = extractFromQueryString(FROM_J_QUERY_NAME, '') === 'true';
    if (fromJ) {
        writeLocallyStored(FROM_J_QUERY_NAME, fromJ);
        removeFromQuery(FROM_J_QUERY_NAME, false);
        cachedFromJ = fromJ;
    } else {
        cachedFromJ = readLocallyStored(FROM_J_QUERY_NAME, '', false);
    }

    return cachedFromJ;
};
